package org.braunequip.web.util

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.graphics.Image
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.braunequip.web.components.models.*
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text
import org.w3c.fetch.*

/*
    Auth Functions

   fun routeToAuthLogin(context)
   - context = PageContext from original page.
   - Uses page context to send user to backend OAuth login route.
   - Opens page in current page.

   fun routeToAuthLogout(context)
   - context = PageContext from original page.
   - Uses page context to send user to backend OAuth logout route.


*/
fun routeToAuthLogin(context: PageContext) {
    // Use to use context.router.navigateTo(...)
    // However, this used Kobweb's internal router, instead changing the real link now.
    window.location.href = Routes.Admin.API_LOGIN_ROUTE
}

fun routeToAuthLogout(context: PageContext) {
    // Use to use context.router.navigateTo(...)
    // However, this used Kobweb's internal router, instead changing the real link now.
    window.location.href = Routes.Admin.API_LOGOUT_ROUTE
}

suspend fun checkUserJWT(): Boolean {
    val response: Response? = try {
        window.fetch(
            input = Routes.Admin.API_CHECK_ACCESS,
            RequestInit(
                method = "GET",
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error checkUserJWT(): $e")
        null
    }

    return response?.ok == true
}

@Composable
fun isUserLoggedIn(content: @Composable () -> Unit) {
    val context = rememberPageContext()
    var userHasAccess by remember { mutableStateOf(false) }

    LaunchedEffect(key1 = Unit) {
        userHasAccess = checkUserJWT()
        if (!userHasAccess) {
            context.router.navigateTo(Routes.Admin.FRONT_LOGIN_ROUTE)
        }
    }

    if (userHasAccess) {
        content()
    } else {
        Box(
            modifier = Modifier
                .fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {

                Image(
                    modifier = Modifier
                        .width(100.px),
                    src = Res.Image.logo,
                    description = "Logo Image"
                )

                H3(
                    attrs = Modifier
                        .toAttrs()
                ) {
                    Text("Loading...")
                }
            }
        }
    }
}

suspend fun checkUserPin(pin: String): Boolean {
    val pinJSON = Json.encodeToString(pin)

    val response: Response? = try {
        window.fetch(
            input = Routes.Admin.PIN_CHECK_ACCESS,
            RequestInit(
                method = "POST",
                body = pinJSON
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error checkUserPin(): $e")
        null
    }

    return response?.ok == true
}


/*
    ----- ADMIN DATABASE MANAGEMENT FUNCTIONS -----
    User must have JWT, and passed inside the request!
*/

/*
     -------------------------------------

            CONTACT FORM FUNCTIONS

     -------------------------------------
*/

suspend fun fetchContactForms(
    skip: Int,
    onSuccess: (List<ContactsDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.CONTACT}?skip=$skip",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error checkUserJWT(): $e")
        null
    }

    if (response != null) {
        val contactsText = response.text().await()
        val contactsList = Json.decodeFromString<List<ContactsDTO>>(contactsText)
        onSuccess(contactsList)
    } else {
        onError("Error fetching contacts!")
    }
}

suspend fun updateContact(
    contact: ContactsDTO
): Boolean {
    val jsonData = jsonSerializer.encodeToString(contact)

    val response: Response? = try {
        window.fetch(
            input = BackendRoutes.CONTACT,
            RequestInit(
                method = "PUT",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    return response?.ok == true
}

suspend fun deleteSelectedContactForms(
    ids: List<String>
): Boolean {
    return try {
        val response = window.fetch(
            input = "${BackendRoutes.CONTACT}/delete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE,
                body = Json.encodeToString(ids)
            )
        ).await()

        response.ok
    } catch (e: Exception) {
        console.log(e)
        false
    }
}

/*
     -------------------------------------

            TIME OFF REQUEST FUNCTIONS

     -------------------------------------
*/
suspend fun fetchTimeOffRequests(
    skip: Int,
    onSuccess: (List<TimeOffRequestDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.TIME_OFF_REQUEST}?skip=$skip",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error checkUserJWT(): $e")
        null
    }

    if (response != null) {
        val responseText = response.text().await()
        val fetchedList = Json.decodeFromString<List<TimeOffRequestDTO>>(responseText)
        onSuccess(fetchedList)
    } else {
        onError("Error fetching time off requests!")
    }
}

suspend fun updateTimeOffRequest(
    request: TimeOffRequestDTO
): Boolean {
    val jsonData = jsonSerializer.encodeToString(request)

    val response: Response? = try {
        window.fetch(
            input = BackendRoutes.TIME_OFF_REQUEST,
            RequestInit(
                method = "PUT",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    return response?.ok == true
}

suspend fun deleteTimeOffRequests(
    ids: List<String>
): Boolean {
    return try {
        val response = window.fetch(
            input = "${BackendRoutes.TIME_OFF_REQUEST}/delete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE,
                body = Json.encodeToString(ids)
            )
        ).await()

        response.ok
    } catch (e: Exception) {
        console.log(e)
        false
    }
}

/*
     -------------------------------------

           DIESEL FUEL LOGS FUNCTIONS

     -------------------------------------
*/
suspend fun fetchDieselFuelLogs(
    skip: Int,
    onSuccess: (List<DieselFuelLogDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.DIESEL_LOG}?skip=$skip",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error checkUserJWT(): $e")
        null
    }

    if (response != null) {
        val responseText = response.text().await()
        val fetchedList = Json.decodeFromString<List<DieselFuelLogDTO>>(responseText)
        onSuccess(fetchedList)
    } else {
        onError("Error fetching diesel fuel logs!")
    }
}

suspend fun updateDieselLog(
    dieselLog: DieselFuelLogDTO
): Boolean {
    val jsonData = jsonSerializer.encodeToString(dieselLog)

    val response: Response? = try {
        window.fetch(
            input = BackendRoutes.DIESEL_LOG,
            RequestInit(
                method = "PUT",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    return response?.ok == true
}

suspend fun deleteDieselFuelLogs(
    ids: List<String>
): Boolean {
    return try {
        val response = window.fetch(
            input = "${BackendRoutes.DIESEL_LOG}/delete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE,
                body = Json.encodeToString(ids)
            )
        ).await()

        response.ok
    } catch (e: Exception) {
        console.log(e)
        false
    }
}


/*
     -------------------------------------

           DIESEL FUEL LOGS FUNCTIONS

     -------------------------------------
*/
suspend fun fetchEquipmentRepairs(
    skip: Int,
    onSuccess: (List<EquipRepairRequestDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.EQUIP_REPAIR}?skip=$skip",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error checkUserJWT(): $e")
        null
    }

    if (response != null) {
        val responseText = response.text().await()
        val fetchedList = Json.decodeFromString<List<EquipRepairRequestDTO>>(responseText)
        onSuccess(fetchedList)
    } else {
        onError("Error fetching diesel fuel logs!")
    }
}

suspend fun updateEquipmentRepair(
    equipRepair: EquipRepairRequestDTO
): Boolean {
    val jsonData = jsonSerializer.encodeToString(equipRepair)

    val response: Response? = try {
        window.fetch(
            input = BackendRoutes.EQUIP_REPAIR,
            RequestInit(
                method = "PUT",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    return response?.ok == true
}

suspend fun deleteEquipmentRepairs(
    ids: List<String>
): Boolean {
    return try {
        val response = window.fetch(
            input = "${BackendRoutes.EQUIP_REPAIR}/delete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE,
                body = Json.encodeToString(ids)
            )
        ).await()

        response.ok
    } catch (e: Exception) {
        console.log(e)
        false
    }
}

/*
     -------------------------------------

           EMPLOYEE PINS FUNCTIONS

     -------------------------------------
*/
suspend fun createEmployeePin(
    pinDTO: PinDTO,
    onFinish: (String) -> Unit
) {
    val jsonSerializer = Json { encodeDefaults = true }
    val jsonData = jsonSerializer.encodeToString(pinDTO)

    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.EMPLOYEE_PINS}/new",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    if (response?.ok == true) {
        onFinish("Submitted!")
    } else {
        onFinish("Error, try again later!")
    }
}

suspend fun fetchEmployeePins(
    skip: Int,
    onSuccess: (List<PinDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.EMPLOYEE_PINS}?skip=$skip",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error Fetching Employee Pins!: $e")
        null
    }

    if (response != null) {
        val pinsResponseText = response.text().await()
        val contactsList = Json.decodeFromString<List<PinDTO>>(pinsResponseText)
        onSuccess(contactsList)
    } else {
        onError("Error fetching pins!")
    }
}

suspend fun updateEmployeePin(
    pin: PinDTO
): Boolean {
    val jsonData = jsonSerializer.encodeToString(pin)

    val response: Response? = try {
        window.fetch(
            input = BackendRoutes.EMPLOYEE_PINS,
            RequestInit(
                method = "PUT",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    return response?.ok == true
}

suspend fun deleteSelectedEmployeePins(
    ids: List<String>
): Boolean {
    return try {
        val response = window.fetch(
            input = "${BackendRoutes.EMPLOYEE_PINS}/delete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE,
                body = Json.encodeToString(ids)
            )
        ).await()

        response.ok
    } catch (e: Exception) {
        console.log(e)
        false
    }
}

/*
     -------------------------------------

           EMPLOYEES FUNCTIONS

     -------------------------------------
*/
suspend fun createEmployee(
    employeeDTO: EmployeeDTO,
    onFinish: (String) -> Unit
) {
    val jsonSerializer = Json { encodeDefaults = true }
    val jsonData = jsonSerializer.encodeToString(employeeDTO)

    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.EMPLOYEES}/new",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    if (response?.ok == true) {
        onFinish("Submitted!")
    } else {
        onFinish("Error, try again later!")
    }
}

suspend fun fetchEmployees(
    skip: Int,
    onSuccess: (List<EmployeeDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.EMPLOYEES}?skip=$skip",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error Fetching Employees!: $e")
        null
    }

    if (response != null) {
        val pinsResponseText = response.text().await()
        val employeesList = Json.decodeFromString<List<EmployeeDTO>>(pinsResponseText)
        onSuccess(employeesList)
    } else {
        onError("Error fetching pins!")
    }
}
suspend fun updateEmployee(
    employee: EmployeeDTO
): Boolean {
    val jsonData = jsonSerializer.encodeToString(employee)

    val response: Response? = try {
        window.fetch(
            input = BackendRoutes.EMPLOYEES,
            RequestInit(
                method = "PUT",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    return response?.ok == true
}

suspend fun deleteSelectedEmployees(
    ids: List<String>
): Boolean {
    return try {
        val response = window.fetch(
            input = "${BackendRoutes.EMPLOYEES}/delete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE,
                body = Json.encodeToString(ids)
            )
        ).await()

        response.ok
    } catch (e: Exception) {
        console.log(e)
        false
    }
}

/*
     -------------------------------------

           ASSETS FUNCTIONS

     -------------------------------------
*/
suspend fun createAsset(
    assetDTO: AssetDTO,
    onFinish: (String) -> Unit
) {
    val jsonSerializer = Json { encodeDefaults = true }
    val jsonData = jsonSerializer.encodeToString(assetDTO)

    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.ASSETS}/new",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    if (response?.ok == true) {
        onFinish("Submitted!")
    } else {
        onFinish("Error, try again later!")
    }
}

suspend fun fetchAssets(
    skip: Int,
    onSuccess: (List<AssetDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.ASSETS}?skip=$skip",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error Fetching Assets!: $e")
        null
    }

    if (response != null) {
        val pinsResponseText = response.text().await()
        val assetsList = Json.decodeFromString<List<AssetDTO>>(pinsResponseText)
        onSuccess(assetsList)
    } else {
        onError("Error fetching pins!")
    }
}

suspend fun fetchAssetById(
    id: String,
    onSuccess: (AssetDTO) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.ASSETS}/asset/$id",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error Fetching Asset: $id!: $e")
        null
    }

    if (response != null) {
        val pinsResponseText = response.text().await()
        val assetsList = Json.decodeFromString<AssetDTO>(pinsResponseText)
        onSuccess(assetsList)
    } else {
        onError("Error Fetching Asset Details for ID: $id!")
    }
}

suspend fun updateAsset(
    asset: AssetDTO
): Boolean {
    val jsonData = jsonSerializer.encodeToString(asset)

    val response: Response? = try {
        window.fetch(
            input = BackendRoutes.ASSETS,
            RequestInit(
                method = "PUT",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    return response?.ok == true
}

suspend fun deleteSelectedAssets(
    ids: List<String>
): Boolean {
    return try {
        val response = window.fetch(
            input = "${BackendRoutes.ASSETS}/delete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE,
                body = Json.encodeToString(ids)
            )
        ).await()

        response.ok
    } catch (e: Exception) {
        console.log(e)
        false
    }
}

/*
    Asset ERoad Functions
 */
suspend fun updateAssetsWithERoadData(
    onSuccess: (String) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.ASSETS}/eroad/",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error Updating All Assets with ERoad information!: $e")
        null
    }

    if (response != null) {
        val responseText = response.text().await()
        if (response.ok) {
            onSuccess(responseText)
        } else {
            onError(responseText)
        }
    } else {
        onError("Error Receiving ERoad Data. Try again later!")
    }
}

suspend fun updateFetchAssetWithERoadData(
    id: String,
    onSuccess: (AssetDTO) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.ASSETS}/eroad/$id",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error Fetching Asset: $id!: $e")
        null
    }

    if (response != null) {
        val responseText = response.text().await()
        try {
            val assetsList = Json.decodeFromString<AssetDTO>(responseText)
            onSuccess(assetsList)
        } catch (e: Exception) {
            onError(responseText)
        }
    } else {
        onError("Error Receiving ERoad Data. Try again later!")
    }
}